<template>
  <div class="bio-section">
    <h2 class="section-title">Instructor Bio</h2>
    <div class="bio-tabs">
      <button 
        :class="['bio-tab', { active: activeBioTab === 'en' }]" 
        @click="$emit('update-bio-tab', 'en')"
      >
        <i class="fas fa-language"></i> English
      </button>
      <button 
        :class="['bio-tab', { active: activeBioTab === 'ar' }]" 
        @click="$emit('update-bio-tab', 'ar')"
      >
        <i class="fas fa-language"></i> Arabic
      </button>
    </div>

    <div class="bio-content">
      <div v-show="activeBioTab === 'en'" class="bio">
        <div class="bio-edit-container">
          <div v-if="!isEditingEnglishBio" class="bio-display">
            <div v-html="instructor.bio || 'No English bio available.'" class="bio-text"></div>
            <button @click="startEditingEnglishBio" class="edit-bio-button" title="Edit English Bio">
              <i class="fas fa-edit"></i>
            </button>
          </div>
          <div v-else class="bio-editor">
            <QuillEditor v-model="editForm.bio" placeholder="Enter instructor bio in English" />
            <div class="bio-edit-actions">
              <button @click="saveEnglishBio" class="primary-button save-bio-button" :disabled="updating">
                <i class="fas fa-check"></i> Save
              </button>
              <button @click="cancelEditingEnglishBio" class="secondary-button cancel-bio-button">
                <i class="fas fa-times"></i> Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-show="activeBioTab === 'ar'" class="bio" dir="rtl">
        <div class="bio-edit-container">
          <div v-if="!isEditingArabicBio" class="bio-display">
            <div v-html="instructor.bio_ar || 'لا توجد سيرة ذاتية متاحة بالعربية.'" class="bio-text"></div>
            <button @click="startEditingArabicBio" class="edit-bio-button" title="تعديل السيرة الذاتية بالعربية">
              <i class="fas fa-edit"></i>
            </button>
          </div>
          <div v-else class="bio-editor">
            <QuillEditor v-model="editForm.bio_ar" placeholder="Enter instructor bio in Arabic" dir="rtl" />
            <div class="bio-edit-actions">
              <button @click="saveArabicBio" class="primary-button save-bio-button" :disabled="updating">
                <i class="fas fa-check"></i> حفظ
              </button>
              <button @click="cancelEditingArabicBio" class="secondary-button cancel-bio-button">
                <i class="fas fa-times"></i> إلغاء
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import QuillEditor from '@/components/QuillEditor.vue';

export default {
  name: 'InstructorBio',
  components: {
    QuillEditor
  },
  props: {
    instructor: {
      type: Object,
      required: true
    },
    activeBioTab: {
      type: String,
      required: true
    }
  },
  setup(props, { emit }) {
    const isEditingEnglishBio = ref(false);
    const isEditingArabicBio = ref(false);
    const updating = ref(false);
    
    const editForm = ref({
      bio: '',
      bio_ar: ''
    });

    const startEditingEnglishBio = () => {
      editForm.value.bio = props.instructor.bio || '';
      isEditingEnglishBio.value = true;
    };

    const startEditingArabicBio = () => {
      editForm.value.bio_ar = props.instructor.bio_ar || '';
      isEditingArabicBio.value = true;
    };

    const saveEnglishBio = async () => {
      updating.value = true;
      try {
        await emit('update-bio', { bio: editForm.value.bio });
        isEditingEnglishBio.value = false;
      } finally {
        updating.value = false;
      }
    };

    const saveArabicBio = async () => {
      updating.value = true;
      try {
        await emit('update-bio', { bio_ar: editForm.value.bio_ar });
        isEditingArabicBio.value = false;
      } finally {
        updating.value = false;
      }
    };

    const cancelEditingEnglishBio = () => {
      isEditingEnglishBio.value = false;
    };

    const cancelEditingArabicBio = () => {
      isEditingArabicBio.value = false;
    };

    return {
      isEditingEnglishBio,
      isEditingArabicBio,
      updating,
      editForm,
      startEditingEnglishBio,
      startEditingArabicBio,
      saveEnglishBio,
      saveArabicBio,
      cancelEditingEnglishBio,
      cancelEditingArabicBio
    };
  }
};
</script>

<style scoped>
.bio-section {
  margin-bottom: 2rem;
}

.section-title {
  margin: 0 0 1.25rem;
  color: #1e293b;
  font-size: 1.375rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.section-title i {
  color: #3b82f6;
  font-size: 1.25rem;
}

.bio-tabs {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 0.5rem;
}

.bio-tab {
  padding: 0.5rem 1rem;
  border: none;
  background: none;
  cursor: pointer;
  color: #64748b;
  font-weight: 500;
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: color 0.2s;
}

.bio-tab.active {
  color: #3b82f6;
}

.bio-tab.active::after {
  content: '';
  position: absolute;
  bottom: -0.5rem;
  left: 0;
  right: 0;
  height: 2px;
  background: #3b82f6;
}

.bio-content {
  background: white;
  border-radius: 0.75rem;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  min-height: 200px;
}

.bio {
  line-height: 1.6;
  color: #475569;
}

.bio[dir="rtl"] {
  text-align: right;
}

.bio-text {
  min-height: 100px;
}

.bio-edit-container {
  position: relative;
}

.bio-display {
  position: relative;
  min-height: 100px;
  transition: all 0.2s;
}

.edit-bio-button {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  padding: 0.5rem;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  color: #64748b;
  cursor: pointer;
  opacity: 0;
  transition: all 0.2s;
}

.bio-display:hover .edit-bio-button {
  opacity: 1;
}

.edit-bio-button:hover {
  color: #3b82f6;
  border-color: #3b82f6;
  background: #f8fafc;
}

.bio-editor {
  margin-top: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  overflow: hidden;
}

.bio-edit-actions {
  display: flex;
  gap: 0.75rem;
  padding: 0.75rem;
  justify-content: flex-end;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}

@media (max-width: 576px) {
  .bio-tabs {
    overflow-x: auto;
    padding-bottom: 0.5rem;
  }
}
</style> 